const SCHEMA_CACHE_PREFIX = "schema:";
const SCHEMA_CACHE_TTL_MS = 15 * 60 * 1000;

interface CachedSchema<T> {
  data: T;
  timestamp: number;
}

/**
 * Fetches a table schema, reusing a session-scoped cache when still fresh.
 */
export async function fetchCachedSchema<T>(
  schemaUrl: string,
  getAccessHeader: (addContentType?: boolean) => Promise<Headers>,
): Promise<T> {
  const cacheKey = `${SCHEMA_CACHE_PREFIX}${schemaUrl}`;
  const cachedRaw = sessionStorage.getItem(cacheKey);

  if (cachedRaw) {
    try {
      const cached = JSON.parse(cachedRaw) as CachedSchema<T>;
      if (Date.now() - cached.timestamp < SCHEMA_CACHE_TTL_MS) {
        return cached.data;
      }
    } catch {
      sessionStorage.removeItem(cacheKey);
    }
  }

  const response = await fetch(schemaUrl, {
    method: "GET",
    headers: await getAccessHeader(false),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch schema from ${schemaUrl}`);
  }

  const data = (await response.json()) as T;
  const entry: CachedSchema<T> = { data, timestamp: Date.now() };
  sessionStorage.setItem(cacheKey, JSON.stringify(entry));

  return data;
}

/**
 * Drops cached schemas so the next fetch reflects admin-side changes.
 */
export function invalidateSchemaCache(schemaUrl?: string): void {
  if (schemaUrl) {
    sessionStorage.removeItem(`${SCHEMA_CACHE_PREFIX}${schemaUrl}`);
    return;
  }

  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(SCHEMA_CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}
